import { useEffect, useState } from "react";
import { Upload } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useLang } from "../lib/i18n";
import { CONTACT } from "../lib/data";
import Shell, { Btn, Card, Input, Pill, Select, Toggle } from "../components/dash/Shell";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");

export default function TechnicianDashboard() {
  const { user } = useAuth();
  const { lang, a } = useLang();
  const d = a.tech;
  const [tech, setTech] = useState(null);
  const [services, setServices] = useState([]);
  const [plans, setPlans] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [phone, setPhone] = useState("");
  const [plan, setPlan] = useState("monthly");
  const [proof, setProof] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [t, s, p, j] = await Promise.all([api.get("/technician/me"), api.get("/services?all=true"), api.get("/plans"), api.get("/technician/bookings")]);
    setTech(t.data); setPhone(t.data.phone || ""); setServices(s.data); setPlans(p.data.filter((x) => x.id !== "trial")); setJobs(j.data);
  };
  useEffect(() => { load().catch((e) => toast.error(errMsg(e))); }, []);

  if (!tech) return <Shell title="…"><p className="text-slate-400 text-sm">Loading…</p></Shell>;

  const sub = tech.subscription;
  const subTone = sub.status === "active" ? "mint" : sub.status === "trial" ? "gold" : "red";

  const toggleService = async (id, on) => {
    const next = on ? [...tech.services, id] : tech.services.filter((s) => s !== id);
    try {
      const { data } = await api.patch("/technician/services", { services: next });
      setTech(data);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const savePhone = async () => {
    try { const { data } = await api.patch("/technician/me", { phone }); setTech(data); toast.success("Saved"); } catch (e) { toast.error(errMsg(e)); }
  };

  const requestSub = async () => {
    if (!proof) return toast.error(d.upload);
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", proof);
      const { data: f } = await api.post("/files/upload?kind=payment-proof", fd);
      const { data } = await api.post("/technician/subscription/request", { plan, proof_file_id: f.id });
      setTech(data); setProof(null); toast.success(d.requested);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const markDone = async (id) => {
    try { await api.patch(`/technician/bookings/${id}`, { status: "completed" }); setJobs((j) => j.map((x) => (x.id === id ? { ...x, status: "completed" } : x))); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Shell title={`${d.hello}, ${user?.name?.split(" ")[0] || ""}`}>
      <div className="grid lg:grid-cols-12 gap-6">
        <Card className="lg:col-span-4" testid="tech-status-card">
          <div className="flex flex-wrap gap-2">
            <Pill tone={tech.approved ? "mint" : "gold"} testid="tech-approval-pill">{tech.approved ? d.approved : d.pendingApproval}</Pill>
            <Pill tone={subTone} testid="tech-subscription-pill">{d[sub.status] || sub.status}</Pill>
          </div>
          <dl className="mt-6 space-y-3 text-sm">
            <div className="flex justify-between text-slate-300"><dt>{d.plan}</dt><dd className="text-white capitalize" data-testid="tech-plan">{sub.plan}</dd></div>
            <div className="flex justify-between text-slate-300"><dt>{d.expires}</dt><dd className="text-white" data-testid="tech-expires">{fmt(sub.expires_at)}</dd></div>
          </dl>
          <div className="mt-6">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">{d.phone}</p>
            <div className="flex gap-2 mt-2">
              <Input data-testid="tech-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="98xxxxxxxx" />
              <Btn variant="ghost" data-testid="tech-phone-save" onClick={savePhone}>{d.savePhone}</Btn>
            </div>
          </div>
        </Card>

        <Card className="lg:col-span-8" title={d.services} sub={d.servicesSub} testid="tech-services-card">
          <ul className="grid sm:grid-cols-2 gap-3">
            {services.map((s) => (
              <li key={s.id} className={`flex items-center justify-between rounded-2xl border px-4 py-3 ${s.enabled ? "border-white/10" : "border-white/5 opacity-50"}`}>
                <div>
                  <p className="text-white text-sm font-medium">{s.name[lang]}</p>
                  <p className="font-mono text-[10px] text-slate-500 mt-0.5">₹{s.price}{!s.enabled && " · OFF by admin"}</p>
                </div>
                <Toggle testid={`tech-service-toggle-${s.id}`} on={tech.services.includes(s.id)} onChange={(on) => toggleService(s.id, on)} />
              </li>
            ))}
          </ul>
        </Card>

        <Card className="lg:col-span-5" title={d.subscription} sub={d.subSub} testid="tech-subscription-card">
          <div className="rounded-2xl border border-gold/30 bg-gold/5 px-4 py-3">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold">{d.upi}</p>
            <p className="text-white font-semibold mt-1">{CONTACT.phoneDisplay}</p>
          </div>
          {sub.requested_plan ? (
            <p data-testid="tech-sub-requested" className="mt-5 text-mint text-sm">{d.requested} · <span className="capitalize">{sub.requested_plan}</span></p>
          ) : (
            <div className="mt-5 space-y-3">
              <Select data-testid="tech-plan-select" value={plan} onChange={(e) => setPlan(e.target.value)}>
                {plans.map((p) => <option key={p.id} value={p.id}>{p.name[lang]} — ₹{p.price} / {p.days} {d.perDays}</option>)}
              </Select>
              <label className="flex items-center gap-3 rounded-xl border border-dashed border-white/20 px-4 py-3 cursor-pointer hover:border-gold/50">
                <Upload size={16} className="text-gold" />
                <span className="text-sm text-slate-300 truncate">{proof ? proof.name : d.upload}</span>
                <input data-testid="tech-proof-input" type="file" accept="image/*" className="hidden" onChange={(e) => setProof(e.target.files?.[0] || null)} />
              </label>
              <Btn data-testid="tech-sub-request-button" disabled={busy} onClick={requestSub} className="w-full">{d.request}</Btn>
            </div>
          )}
        </Card>

        <Card className="lg:col-span-7" title={d.jobs} testid="tech-jobs-card">
          {jobs.length === 0 ? (
            <p data-testid="tech-no-jobs" className="text-slate-400 text-sm">{d.noJobs}</p>
          ) : (
            <ul className="space-y-3">
              {jobs.map((j) => (
                <li key={j.id} data-testid={`tech-job-${j.id}`} className="rounded-2xl border border-white/10 p-4 flex flex-wrap items-center gap-3 justify-between">
                  <div>
                    <p className="text-white text-sm font-medium">{j.service_name[lang]} · <span className="font-mono text-gold">{j.id}</span></p>
                    <p className="text-slate-400 text-xs mt-1">{j.date} {j.time} · {j.name} · {j.phone}</p>
                    <p className="text-slate-500 text-xs">{j.address}</p>
                  </div>
                  {j.status === "completed" ? <Pill tone="mint">{d.done}</Pill> : <Btn variant="ghost" data-testid={`tech-job-done-${j.id}`} onClick={() => markDone(j.id)}>{d.markDone}</Btn>}
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </Shell>
  );
}
